// Shared helpers for the hedge bracket picker. Lives outside both the
// dialog (/pairs/:slug/:symbol Manual Hedge Open) and the terminal's
// OrderPanel so the SL/TP normalization rules are written exactly once.

import type { SpreadData } from "@/hooks/use-live-feed";

/** Aliases the broker-radar feed publishes for indices. The picker
 *  uses these to look up the live quote regardless of which name the
 *  operator typed. Add to this map when a new index alias shows up. */
export const ALIAS_MAP: Record<string, string> = {
  NDXUSD: "USTEC",
  US100: "USTEC",
  NASDAQ100: "USTEC",
  NQ: "USTEC",
  SPXUSD: "US500",
  SP500: "US500",
  SPX: "US500",
};

/** Fallback contract sizes — only used when the pair row's
 *  `contractSize` is null (pre-validate, or driver couldn't surface
 *  it). Real broker-reported values always win when present. */
export const KNOWN_CONTRACT_SIZES: Record<string, number> = {
  EURUSD: 100000,
  GBPUSD: 100000,
  USDJPY: 100000,
  USDCHF: 100000,
  AUDUSD: 100000,
  USDCAD: 100000,
  NZDUSD: 100000,
  EURJPY: 100000,
  GBPJPY: 100000,
  EURGBP: 100000,
  XAUUSD: 100,
  XAGUSD: 5000,
  BTCUSD: 1,
  ETHUSD: 1,
  WTIUSD: 1000,
};

/** Symmetric quick-fill chips. Click one → SL = mid·(1−p), TP = mid·(1+p). */
export const SYMMETRIC_QUICK_FILL_PERCENTS = [0.1, 0.25, 0.5, 1, 2] as const;

/** Asymmetric quick-fill presets. TP wider than SL — common when
 *  fading a news pop or scaling out a winner while keeping a tight
 *  protective stop. Each tuple is `[slPct, tpPct]`. */
export const ASYMMETRIC_QUICK_FILL_PRESETS: Array<[number, number]> = [
  [0.25, 0.5],
  [0.25, 1],
  [0.5, 1],
  [0.5, 2],
  [1, 2],
  [1, 3],
];

export function normalizeSymbol(symbol: string): string {
  const upper = symbol.trim().toUpperCase();
  return ALIAS_MAP[upper] ?? upper;
}

export function priceMapKey(item: SpreadData): string {
  return normalizeSymbol(item.symbol);
}

export function symbolMid(item: SpreadData | undefined | null): number | null {
  if (!item || !item.isOpen) return null;
  return (item.bid + item.ask) / 2;
}

export function parsePositive(value: string): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/** Format a price for display: pad to 8 dp, strip trailing zeros and a
 *  trailing dot. So 2497.32 stays "2497.32", 1.12345 stays "1.12345",
 *  and 0.1+0.2-noise like 1.30000000000004 becomes "1.30000000". */
export function fmtPrice(p: number): string {
  return p.toFixed(8).replace(/\.?0+$/, "");
}

/**
 * Mirror a positive `(slDistance, tpDistance)` pair around `ref` into
 * absolute prices for the requested `side`. The picker normalizes
 * brackets to one positive distance pair (always SL < ref < TP for buys
 * and TP < ref < SL for sells). Both call sites — terminal OrderPanel
 * fan-out and the dialog — need the same conversion when sending one
 * leg's bracket to the other leg, so the math lives here.
 */
export function mirrorAbsoluteSlTp(
  ref: number,
  slDistance: number,
  tpDistance: number,
  side: "buy" | "sell",
): { sl: number; tp: number } {
  if (side === "buy") {
    return { sl: ref - slDistance, tp: ref + tpDistance };
  }
  return { sl: ref + slDistance, tp: ref - tpDistance };
}
